import { ActionTree } from "vuex";
import { RootState } from "../store";
import { PlanTrailState } from "./PlanTrailModule";
import { ServiceProvider } from "../../services/ServiceProvider";
import { generateID } from "../../../../utils/helpers/IDGenerator";
import { plainToClass } from "class-transformer";
import { MarkerFeature } from "../../../../utils/models/MarkerFeature";
import mapboxgl from "mapbox-gl";

/**
 * Refactored actions using the new service architecture
 */
const refactoredActions: ActionTree<PlanTrailState, RootState> = {
  /**
   * Initialize services when the map is ready
   */
  async initializeServices(store) {
    const map = window.map;
    const layerRegistry = window.layerRegistry;
    
    if (!map || !layerRegistry) {
      throw new Error("Map and layerRegistry must be initialized first");
    }
    
    // Initialize production services
    const services = ServiceProvider.getInstance().initializeProduction(map, layerRegistry);
    
    // Set up event listeners
    services.eventBus.onMarkerDragEnd(async (event) => {
      await store.dispatch('handleMarkerDragEnd', event);
    });
    
    services.eventBus.onLineClick(async (event) => {
      await store.dispatch('handleLineClick', event);
    });
    
    services.eventBus.onMapHover(async (event) => {
      await store.dispatch('handleMapHover', event);
    });
    
    services.eventBus.onMapClick(async (event) => {
      await store.dispatch('handleMapClick', event);
    });
  },
  
  /**
   * Refactored mouse moved handler using services
   */
  mouseMovedOnMapRefactored: (() => {
    let hoverMarker: MarkerFeature | null = null;
    let lastHoverState = { nearLine: false, point: null as { lng: number; lat: number } | null };
    let updateTimer: NodeJS.Timeout | null = null;
    
    return async function(store, e: mapboxgl.MapMouseEvent) {
      // Check if services are initialized
      let services;
      try {
        services = ServiceProvider.getInstance().getServices();
      } catch (error) {
        // Services not initialized yet, skip this mouse move
        return;
      }
      const { mapService, routeManager, eventBus } = services;
    
    // Validate event data
    if (!e || !e.lngLat || typeof e.lngLat.lng !== 'number' || typeof e.lngLat.lat !== 'number' ||
        isNaN(e.lngLat.lng) || isNaN(e.lngLat.lat)) {
      console.warn('Invalid mouse event data:', e);
      return;
    }
    
    // Debug logging removed - validation in place
    
    // Only show hover in plan mode and not in removal mode
    if (!store.state.planMode || store.state.removalMode) {
      store.commit('setNearLine', false);
      store.commit('setHoverPoint', null);
      mapService.removeMarker('hover-marker');
      mapService.setCursor(store.state.planMode ? 'crosshair' : 'pointer');
      return;
    }
    
    // Check if hovering over a marker
    const markerLayers = Object.keys(store.state.routes)
      .flatMap(routeId => {
        const route = store.state.routes[routeId];
        return route.markers.map((_, idx) => `route-${routeId}-marker-${idx}`);
      });
    
    const markerFeatures = mapService.queryRenderedFeatures(e.point, { layers: markerLayers });
    
    if (markerFeatures.length > 0) {
      // Clear hover state when over a marker
      if (lastHoverState.nearLine) {
        store.commit('setNearLine', false);
        store.commit('setHoverPoint', null);
        if (updateTimer) {
          clearTimeout(updateTimer);
          updateTimer = null;
        }
        if (hoverMarker) {
          mapService.removeMarker('hover-marker');
          hoverMarker = null;
        }
        lastHoverState = { nearLine: false, point: null };
      }
      eventBus.emitMapHover({ point: [e.lngLat.lng, e.lngLat.lat], nearLine: false });
      return;
    }
    
    // Check proximity to lines
    const buffer = 15; // pixels
    const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
      [e.point.x - buffer, e.point.y - buffer],
      [e.point.x + buffer, e.point.y + buffer]
    ];
    
    const lineLayers = Object.keys(store.state.routes)
      .flatMap(routeId => {
        const route = store.state.routes[routeId];
        return route.lineStrings.map((_, idx) => `route-${routeId}-line-${idx}`);
      });
    
    const lineFeatures = mapService.queryRenderedFeaturesInBox(bbox, { layers: lineLayers });
    
    if (lineFeatures.length > 0) {
      // Line features found, proceed with validation
      
      // Validate routes before processing
      const validRoutes = {};
      for (const [routeId, route] of Object.entries(store.state.routes)) {
        if (route && route.markers && route.lineStrings) {
          validRoutes[routeId] = route;
        }
      }
      
      if (Object.keys(validRoutes).length === 0) {
        console.warn('No valid routes found');
        return;
      }
      
      // Use RouteManager to find the nearest line
      const nearestLine = routeManager.findNearestLine(
        validRoutes,
        [e.lngLat.lng, e.lngLat.lat],
        0.05 // threshold in km
      );
      
      // Process nearest line result
      
      if (nearestLine) {
        // Check if the nearest line belongs to the active route
        const activeRouteId = store.getters['activeRouteId'];
        const isActiveRoute = nearestLine.routeId === activeRouteId;
        
        // Only show hover marker for active route
        if (!isActiveRoute) {
          // Clear any existing hover state for inactive routes
          if (lastHoverState.nearLine) {
            store.commit('setNearLine', false);
            store.commit('setHoverPoint', null);
            if (updateTimer) {
              clearTimeout(updateTimer);
              updateTimer = null;
            }
            if (hoverMarker) {
              mapService.removeMarker('hover-marker');
              hoverMarker = null;
            }
            lastHoverState = { nearLine: false, point: null };
          }
          mapService.setCursor('crosshair');
          return;
        }
        
        // Calculate pixel distance to determine if we're close enough
        const nearestPixel = mapService.project([
          nearestLine.insertionPoint.lng,
          nearestLine.insertionPoint.lat
        ]);
        
        const distance = Math.sqrt(
          Math.pow(e.point.x - nearestPixel.x, 2) +
          Math.pow(e.point.y - nearestPixel.y, 2)
        );
        
        if (distance < buffer) {
          const newPoint = nearestLine.insertionPoint;
          
          // Check if hover state needs updating
          const pointChanged = !lastHoverState.point || 
            Math.abs(lastHoverState.point.lng - newPoint.lng) > 0.00001 ||
            Math.abs(lastHoverState.point.lat - newPoint.lat) > 0.00001;
          
          if (!lastHoverState.nearLine || pointChanged) {
            store.commit('setNearLine', true);
            // Include routeId in the hover point
            store.commit('setHoverPoint', {
              ...newPoint,
              routeId: nearestLine.routeId
            });
            
            // Clear any pending update
            if (updateTimer) {
              clearTimeout(updateTimer);
            }
            
            // Update marker with a small delay to batch rapid movements
            updateTimer = setTimeout(async () => {
              try {
                if (!hoverMarker) {
                  // Create new hover marker
                  hoverMarker = await MarkerFeature.fromCoordinates([
                    newPoint.lng,
                    newPoint.lat
                  ], true);
                  
                  await mapService.addMarker('hover-marker', hoverMarker, {
                    isActive: true,
                    opacity: 0.7
                  });
                } else {
                  // Update existing marker position
                  mapService.updateMarkerPosition('hover-marker', [newPoint.lng, newPoint.lat]);
                }
              } catch (error) {
                console.error('Error updating hover marker:', error);
              }
            }, 10); // 10ms delay to batch updates
            
            lastHoverState = { nearLine: true, point: newPoint };
          }
          
          // Update cursor
          mapService.setCursor('copy');
          
          // Emit hover event
          eventBus.emitMapHover({
            point: [e.lngLat.lng, e.lngLat.lat],
            nearLine: true,
            lineId: nearestLine.lineId,
            insertPoint: [nearestLine.insertionPoint.lng, nearestLine.insertionPoint.lat]
          });
          
          return;
        }
      }
    }
    
    // Not near any line - only clear if previously set
    if (lastHoverState.nearLine) {
      store.commit('setNearLine', false);
      store.commit('setHoverPoint', null);
      
      // Clear any pending update
      if (updateTimer) {
        clearTimeout(updateTimer);
        updateTimer = null;
      }
      
      if (hoverMarker) {
        mapService.removeMarker('hover-marker');
        hoverMarker = null;
      }
      
      lastHoverState = { nearLine: false, point: null };
    }
    
    mapService.setCursor('crosshair');
    
    eventBus.emitMapHover({
      point: [e.lngLat.lng, e.lngLat.lat],
      nearLine: false
    });
    };
  })(),
  
  /**
   * Handle marker drag end event
   */
  async handleMarkerDragEnd(store, event) {
    // NOTE: We do NOT call updateUndo here because updateLineStringsFromMarkerMove
    // creates provisional lines that will be replaced by GraphHopper responses.
    // The undo state will be saved in replaceProvisionalLine once the real route arrives.
    // This prevents duplicate undo states and avoids saving provisional straight lines.

    // Update marker in state
    const marker = event.marker;
    marker.markerGeometry.coordinates = event.newPosition;
    marker.markerProperties.isSnap = store.state.isSnap;

    await store.commit('updateMarker', marker);

    // Find marker index and update line strings
    const index = store.state.markers.findIndex(m => m.markerProperties.id === marker.markerProperties.id);
    await store.dispatch('updateLineStringsFromMarkerMove', index);
  },
  
  /**
   * Handle line click event
   */
  async handleLineClick(store, event) {
    if (store.state.hoverPoint) {
      // Insert marker at hover point
      await store.dispatch('insertMarkerAtPoint', {
        point: [event.clickPoint[0], event.clickPoint[1]],
        routeId: event.routeId,
        insertIndex: event.insertIndex
      });
    }
  },
  
  /**
   * Handle map hover event
   */
  async handleMapHover(store, event) {
    // This could be used to update UI elements outside the map
    // For example, showing coordinates or distance info
  },
  
  /**
   * Handle map click event
   */
  async handleMapClick(store, event) {
    const services = ServiceProvider.getInstance().getServices();
    const { eventBus } = services;
    
    // Check if clicking on hover point
    if (store.state.hoverPoint) {
      const hoverPoint = store.state.hoverPoint;
      
      // Find which route contains this line
      let targetRouteId: string | null = null;
      for (const [routeId, route] of Object.entries(store.state.routes)) {
        if (route.lineStrings.some(ls => ls.lineStringProperties.id === hoverPoint.lineStringId)) {
          targetRouteId = routeId;
          break;
        }
      }
      
      if (targetRouteId) {
        eventBus.emitLineClick({
          lineId: hoverPoint.lineStringId,
          routeId: targetRouteId,
          clickPoint: [hoverPoint.lng, hoverPoint.lat],
          insertIndex: hoverPoint.insertIndex
        });
      }
    } else {
      // Regular map click - add new marker
      await store.dispatch('addMarkerToActiveRoute', {
        coordinates: event.point
      });
    }
  },
  
  /**
   * Add marker to active route using services
   */
  async addMarkerToActiveRoute(store, { coordinates }) {
    const services = ServiceProvider.getInstance().getServices();
    const { mapService } = services;
    
    if (!store.state.activeRouteKey) {
      return;
    }
    
    const activeRoute = store.state.routes[store.state.activeRouteKey];
    const markerId = generateID();
    const marker = await MarkerFeature.fromCoordinates(coordinates, false);
    marker.markerProperties.id = markerId;
    
    // Add to state
    store.commit('addMarker', marker);
    
    // Add to map
    const markerIndex = activeRoute.markers.length - 1;
    const layerId = `route-${activeRoute.id}-marker-${markerIndex}`;
    
    await mapService.addMarker(layerId, marker, {
      isActive: true,
      isFirst: markerIndex === 0,
      isDraggable: true
    });
    
    // Add line if needed
    if (markerIndex > 0) {
      await store.dispatch('addToPolyline', marker);
    }
  }
};

export default refactoredActions;